import api from './api';
import {
  User,
  TeamMember,
  CreateUserRequest,
  UpdateUserRequest,
  CreateTeamMemberRequest,
  UpdateTeamMemberRequest,
  UpdateTeamMemberRoleRequest,
  ChangePasswordRequest,
} from '../types/user';

export const userService = {
  // ==================== System Users (Admin) ====================

  /**
   * Get all system admin users
   */
  async getSystemAdmins(): Promise<User[]> {
    const { data } = await api.get<User[]>('/api/v1/users/system-admins');
    return data;
  },

  /**
   * Get user by ID
   */
  async getById(id: string): Promise<User> {
    const { data } = await api.get<User>(`/api/v1/users/${id}`);
    return data;
  },

  /**
   * Get current user
   */
  async getMe(): Promise<User> {
    const { data } = await api.get<User>('/api/v1/users/me');
    return data;
  },

  /**
   * Create system user
   */
  async create(request: CreateUserRequest): Promise<User> {
    const { data } = await api.post<User>('/api/v1/users', request);
    return data;
  },

  /**
   * Update user profile
   */
  async update(id: string, request: UpdateUserRequest): Promise<User> {
    const { data } = await api.put<User>(`/api/v1/users/${id}`, request);
    return data;
  },

  /**
   * Activate user
   */
  async activate(id: string): Promise<void> {
    await api.patch(`/api/v1/users/${id}/activate`);
  },

  /**
   * Deactivate user
   */
  async deactivate(id: string): Promise<void> {
    await api.patch(`/api/v1/users/${id}/deactivate`);
  },

  /**
   * Delete user
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/api/v1/users/${id}`);
  },

  // ==================== Team Members (Tenant Users) ====================

  /**
   * Get team members for current tenant
   */
  async getTeamMembers(): Promise<TeamMember[]> {
    const { data } = await api.get<TeamMember[]>('/api/v1/users/team');
    return data;
  },

  /**
   * Create team member
   */
  async createTeamMember(request: CreateTeamMemberRequest): Promise<TeamMember> {
    const { data } = await api.post<TeamMember>('/api/v1/users/team', request);
    return data;
  },

  /**
   * Update team member
   */
  async updateTeamMember(id: string, request: UpdateTeamMemberRequest): Promise<TeamMember> {
    const { data } = await api.put<TeamMember>(`/api/v1/users/team/${id}`, request);
    return data;
  },

  /**
   * Update team member role
   */
  async updateTeamMemberRole(id: string, request: UpdateTeamMemberRoleRequest): Promise<TeamMember> {
    const { data } = await api.put<TeamMember>(`/api/v1/users/team/${id}/role`, request);
    return data;
  },

  /**
   * Delete team member
   */
  async deleteTeamMember(id: string): Promise<void> {
    await api.delete(`/api/v1/users/team/${id}`);
  },

  // ==================== Password Management ====================

  /**
   * Change current user password
   */
  async changePassword(request: ChangePasswordRequest): Promise<void> {
    await api.post('/api/v1/users/me/change-password', request);
  },
};
